import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { productosApi } from '@/services/productosApi';
import { controladoresApi } from '@/services/controladoresApi';
import { pantallaModularApi } from '@/services/pantallaModularApi';
import { cotizacionesApi } from '@/services/cotizacionesApi';
import { inventarioApi } from '@/services/inventarioApi';
import { useDniLookup } from '@/hooks/useDniLookup';
import { useQuotationCalculations } from '@/hooks/useQuotationCalculations';
import { formatCurrency, formatDimension, formatArea, resolveImageUrl } from '@/utils/formatters';
import type { ProductoDto } from '@/types/producto';
import type { ControladorDto } from '@/types/controlador';
import type { PantallaModularDto } from '@/types/pantallaModular';
import type { TipoOperacion, Ubicacion, CotizacionCreateRequest } from '@/types/cotizacion';
import type { DisponibilidadDto } from '@/types/inventario';
import { CanvasBoard } from '@/components/quotation/CanvasBoard';
import { StockAvailabilityBadge } from '@/components/inventario/StockAvailabilityBadge';
import { generateCotizacionPdf } from '@/utils/pdfGenerator';

interface LineItem {
  tipo: 'GABINETE' | 'MODULAR';
  producto: ProductoDto | null;
  pantallaModular: PantallaModularDto | null;
  columnas: number;
  filas: number;
  rotado: boolean;
  precioM2: number;
  cantidad: number;
  anchoDeseadoM: number;
  altoDeseadoM: number;
}

const newPantalla = (): LineItem => ({
  tipo: 'GABINETE',
  producto: null,
  pantallaModular: null,
  columnas: 1,
  filas: 1,
  rotado: false,
  precioM2: 0,
  cantidad: 1,
  anchoDeseadoM: 0,
  altoDeseadoM: 0,
});

export function Cotizacion() {
  const location = useLocation();

  // Client data
  const [dni, setDni] = useState('');
  const [clienteNombre, setClienteNombre] = useState('');
  const [clienteRubro, setClienteRubro] = useState('');
  const [clienteCorreo, setClienteCorreo] = useState('');
  const [clienteAtencion, setClienteAtencion] = useState('');
  const [clienteTelefono, setClienteTelefono] = useState('');
  const [clienteCiudad, setClienteCiudad] = useState('');
  const [clienteRepresentante, setClienteRepresentante] = useState('');
  const [clienteCargoRepresentante, setClienteCargoRepresentante] = useState('');
  const { cliente, nombreFactiliza, loading: dniLoading, searchByDni } = useDniLookup();

  // Quotation data
  const [tipoOperacion, setTipoOperacion] = useState<TipoOperacion>('VENTA');
  const [ubicacion, setUbicacion] = useState<Ubicacion>('LIMA');
  const [costoEnvio, setCostoEnvio] = useState(0);
  const [descuentoActivo, setDescuentoActivo] = useState(false);
  const [porcentajeDescuento, setPorcentajeDescuento] = useState(0);
  const [nombreDescuento, setNombreDescuento] = useState('');
  const [nombreProyecto, setNombreProyecto] = useState('');
  const [notas, setNotas] = useState('');

  // Pantallas (multi)
  const [lineItems, setLineItems] = useState<LineItem[]>([newPantalla()]);

  // Product selection
  const [showProductModal, setShowProductModal] = useState(false);
  const [activeLineIndex, setActiveLineIndex] = useState(0);
  const [tipoSeleccion, setTipoSeleccion] = useState<'GABINETE' | 'MODULAR'>('GABINETE');

  // Controlador
  const [selectedControlador, setSelectedControlador] = useState<ControladorDto | null>(null);
  const [precioControlador, setPrecioControlador] = useState(0);
  const [tipoControladorFiltro, setTipoControladorFiltro] = useState<'SINCRONO' | 'ASINCRONO'>('SINCRONO');

  // Adicionales
  const [adicionales, setAdicionales] = useState<{ descripcion: string; precio: number; cantidad: number }[]>([]);

  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Fetch products
  const { data: productos = [] } = useQuery({
    queryKey: ['productos'],
    queryFn: () => productosApi.findAll(),
  });

  const { data: controladores = [] } = useQuery({
    queryKey: ['controladores'],
    queryFn: controladoresApi.findAll,
  });

  const { data: pantallasModulares = [] } = useQuery({
    queryKey: ['pantallas-modulares'],
    queryFn: pantallaModularApi.findAll,
  });

  const { data: dispProductos = [], isPending: dispProductosPending } = useQuery({
    queryKey: ['inventario-disponibilidad', 'PRODUCTO'],
    queryFn: () => inventarioApi.disponibilidad('PRODUCTO'),
  });
  const { data: dispModulares = [], isPending: dispModularesPending } = useQuery({
    queryKey: ['inventario-disponibilidad', 'PANTALLA_MODULAR'],
    queryFn: () => inventarioApi.disponibilidad('PANTALLA_MODULAR'),
  });
  const { data: dispControladores = [], isPending: dispControladoresPending } = useQuery({
    queryKey: ['inventario-disponibilidad', 'CONTROLADOR'],
    queryFn: () => inventarioApi.disponibilidad('CONTROLADOR'),
  });

  const dispProductosMap = useMemo(() => {
    const m = new Map<number, DisponibilidadDto>();
    for (const d of dispProductos) m.set(d.itemId, d);
    return m;
  }, [dispProductos]);
  const dispModularesMap = useMemo(() => {
    const m = new Map<number, DisponibilidadDto>();
    for (const d of dispModulares) m.set(d.itemId, d);
    return m;
  }, [dispModulares]);
  const dispControladoresMap = useMemo(() => {
    const m = new Map<number, DisponibilidadDto>();
    for (const d of dispControladores) m.set(d.itemId, d);
    return m;
  }, [dispControladores]);

  const handleDniChange = (value: string) => {
    setDni(value);
    searchByDni(value);
  };

  // Reset form when "Nueva Cotización" is clicked from sidebar
  useEffect(() => {
    if (!location.state?.resetAt) return;
    setDni(''); setClienteNombre(''); setClienteRubro(''); setClienteCorreo('');
    setClienteAtencion(''); setClienteTelefono(''); setClienteCiudad('');
    setClienteRepresentante(''); setClienteCargoRepresentante('');
    setTipoOperacion('VENTA'); setUbicacion('LIMA'); setCostoEnvio(0);
    setDescuentoActivo(false); setPorcentajeDescuento(0);
    setNombreDescuento(''); setNombreProyecto(''); setNotas('');
    setLineItems([newPantalla()]);
    setSelectedControlador(null); setPrecioControlador(0); setTipoControladorFiltro('SINCRONO');
    setAdicionales([]);
  }, [location.state?.resetAt]);

  // Auto-fill nombre desde Factiliza (prioridad)
  useEffect(() => {
    if (nombreFactiliza) setClienteNombre(nombreFactiliza);
  }, [nombreFactiliza]);

  // Auto-fill otros datos del cliente desde DB local
  useEffect(() => {
    if (cliente) {
      // Solo sobreescribe el nombre si Factiliza no devolvió nada
      if (!nombreFactiliza) setClienteNombre(cliente.nombre || '');
      setClienteRubro(cliente.rubro || '');
      setClienteCorreo(cliente.correo || '');
      setClienteAtencion(cliente.atencion || '');
      setClienteTelefono(cliente.telefono || '');
      setClienteCiudad(cliente.ciudad || '');
      setClienteRepresentante(cliente.representante || '');
      setClienteCargoRepresentante(cliente.cargoRepresentante || '');
    }
  }, [cliente]);

  // Calculations
  const calculations = useQuotationCalculations({
    lineItems,
    descuentoActivo,
    porcentajeDescuento,
    costoEnvio: ubicacion === 'PROVINCIA' ? costoEnvio : 0,
    precioControlador: selectedControlador ? precioControlador : 0,
    adicionales,
  });

  // Total pixels = sum across all pantallas (× cantidad de cada una)
  const totalPixels = lineItems.reduce((sum, item) => {
    const resString = item.tipo === 'GABINETE'
      ? item.producto?.resolucionGabinete
      : item.pantallaModular?.resolucionModulo;
    if (!resString) return sum;
    const m = resString.match(/^(\d+)[xX×](\d+)$/);
    if (!m) return sum;
    const px = parseInt(m[1]) * item.columnas * parseInt(m[2]) * item.filas;
    return sum + px * Math.max(1, item.cantidad || 1);
  }, 0);

  // Solo relevante para SÍNCRONO
  const requiredPorts = totalPixels > 0 ? Math.ceil(totalPixels / 650000) : 0;

  const filteredControladores = controladores.filter((c) => c.tipo === tipoControladorFiltro);

  const recommendedControlador = (() => {
    if (tipoControladorFiltro === 'SINCRONO') {
      if (requiredPorts <= 0) return null;
      return filteredControladores
        .filter((c) => c.puertos >= requiredPorts)
        .sort((a, b) => a.puertos - b.puertos)[0] ?? null;
    } else {
      if (totalPixels <= 0) return filteredControladores[0] ?? null;
      return filteredControladores
        .filter((c) => c.capacidadPixel != null && c.capacidadPixel * 1_000_000 >= totalPixels)
        .sort((a, b) => (a.capacidadPixel ?? 0) - (b.capacidadPixel ?? 0))[0] ?? null;
    }
  })();

  // Auto-select recommended controlador when it changes
  useEffect(() => {
    if (recommendedControlador && !selectedControlador) {
      setSelectedControlador(recommendedControlador);
    }
  }, [recommendedControlador?.id, tipoControladorFiltro]);

  const updateLineItem = (idx: number, patch: Partial<LineItem>) => {
    setLineItems((prev) => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const addPantalla = () => {
    setLineItems((prev) => [...prev, newPantalla()]);
  };

  const removePantalla = (idx: number) => {
    setLineItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  const movePantalla = (idx: number, dir: -1 | 1) => {
    setLineItems((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const selectProduct = (producto: ProductoDto) => {
    updateLineItem(activeLineIndex, { tipo: 'GABINETE', producto, pantallaModular: null });
    setShowProductModal(false);
  };

  const selectPantallaModular = (pm: PantallaModularDto) => {
    updateLineItem(activeLineIndex, { tipo: 'MODULAR', pantallaModular: pm, producto: null });
    setShowProductModal(false);
  };

  // Calculate cols/rows from desired meters (always floor to fit) for a given pantalla
  const applyDesiredMeters = (idx: number, anchoM: number, altoM: number) => {
    const item = lineItems[idx];
    if (!item) return;
    let anchoCm: number, altoCm: number;
    if (item.tipo === 'MODULAR' && item.pantallaModular) {
      anchoCm = (item.rotado ? item.pantallaModular.altoModuloMm : item.pantallaModular.anchoModuloMm) / 10;
      altoCm = (item.rotado ? item.pantallaModular.anchoModuloMm : item.pantallaModular.altoModuloMm) / 10;
    } else if (item.producto) {
      anchoCm = item.producto.anchoCm;
      altoCm = item.producto.altoCm;
    } else return;
    const cols = Math.max(1, Math.min(100, Math.floor((anchoM * 100) / anchoCm)));
    const rows = Math.max(1, Math.min(100, Math.floor((altoM * 100) / altoCm)));
    updateLineItem(idx, { columnas: cols, filas: rows });
  };

  const handleAnchoDeseado = (idx: number, value: number) => {
    updateLineItem(idx, { anchoDeseadoM: value });
    if (!isNaN(value) && value > 0) applyDesiredMeters(idx, value, lineItems[idx]?.altoDeseadoM || 0);
  };

  const handleAltoDeseado = (idx: number, value: number) => {
    updateLineItem(idx, { altoDeseadoM: value });
    if (!isNaN(value) && value > 0) applyDesiredMeters(idx, lineItems[idx]?.anchoDeseadoM || 0, value);
  };

  const stepLineItem = (idx: number, field: 'columnas' | 'filas' | 'cantidad', delta: number) => {
    const current = lineItems[idx][field];
    const min = 1;
    const max = field === 'cantidad' ? 99 : 100;
    const next = Math.max(min, Math.min(max, (isNaN(current) ? 1 : current) + delta));
    updateLineItem(idx, { [field]: next });
  };

  // Demanda agregada por (tipo, itemId) — usada para mostrar errores inline y bloquear el guardado.
  const demandaPorProducto = useMemo(() => {
    const m = new Map<number, number>();
    for (const li of lineItems) {
      if (li.tipo !== 'GABINETE' || !li.producto) continue;
      const cant = li.cantidad > 0 ? li.cantidad : 1;
      const unidades = (li.columnas || 0) * (li.filas || 0) * cant;
      if (unidades <= 0) continue;
      m.set(li.producto.id, (m.get(li.producto.id) ?? 0) + unidades);
    }
    return m;
  }, [lineItems]);
  const demandaPorModular = useMemo(() => {
    const m = new Map<number, number>();
    for (const li of lineItems) {
      if (li.tipo !== 'MODULAR' || !li.pantallaModular) continue;
      const cant = li.cantidad > 0 ? li.cantidad : 1;
      const unidades = (li.columnas || 0) * (li.filas || 0) * cant;
      if (unidades <= 0) continue;
      m.set(li.pantallaModular.id, (m.get(li.pantallaModular.id) ?? 0) + unidades);
    }
    return m;
  }, [lineItems]);

  const stockErrors = useMemo(() => {
    // Mientras los queries aún están cargando (primer fetch), evita falsos positivos en internet lento.
    if (dispProductosPending || dispModularesPending || dispControladoresPending) return [];
    const errors: { nombre: string }[] = [];
    for (const [id, demanded] of demandaPorProducto) {
      const disponible = dispProductosMap.get(id)?.cantidadDisponible ?? 0;
      if (demanded > disponible) {
        const nombre = lineItems.find((li) => li.producto?.id === id)?.producto?.nombre ?? `Gabinete ${id}`;
        errors.push({ nombre });
      }
    }
    for (const [id, demanded] of demandaPorModular) {
      const disponible = dispModularesMap.get(id)?.cantidadDisponible ?? 0;
      if (demanded > disponible) {
        const nombre = lineItems.find((li) => li.pantallaModular?.id === id)?.pantallaModular?.nombre ?? `Módulo ${id}`;
        errors.push({ nombre });
      }
    }
    if (selectedControlador) {
      const disponible = dispControladoresMap.get(selectedControlador.id)?.cantidadDisponible ?? 0;
      if (disponible < 1) {
        errors.push({ nombre: selectedControlador.nombre });
      }
    }
    return errors;
  }, [demandaPorProducto, demandaPorModular, dispProductosMap, dispModularesMap, dispControladoresMap, selectedControlador, lineItems, dispProductosPending, dispModularesPending, dispControladoresPending]);

  const handleSave = async () => {
    if (!dni || !clienteNombre) {
      toast.error('El DNI y nombre del cliente son obligatorios');
      return;
    }
    const validItems = lineItems.filter((li) => li.producto || li.pantallaModular);
    if (validItems.length === 0) {
      toast.error('Debe seleccionar al menos un gabinete o módulo');
      return;
    }
    if (validItems.some((li) => !li.precioM2 || li.precioM2 <= 0)) {
      toast.error('Cada pantalla debe tener un precio por m² mayor a 0');
      return;
    }
    if (stockErrors.length > 0) {
      const detalle = stockErrors.map((e) => e.nombre).join(', ');
      toast.error(`Stock insuficiente: ${detalle}`);
      return;
    }

    setSaving(true);
    try {
      const firstPrecioM2 = validItems[0].precioM2;
      const request: CotizacionCreateRequest = {
        clienteDni: dni,
        clienteNombre,
        clienteRubro,
        clienteCorreo,
        clienteAtencion,
        clienteTelefono,
        clienteCiudad,
        clienteRepresentante: dni.length === 11 ? clienteRepresentante : undefined,
        clienteCargoRepresentante: dni.length === 11 ? clienteCargoRepresentante : undefined,
        tipoOperacion,
        ubicacion,
        costoEnvio: ubicacion === 'PROVINCIA' ? costoEnvio : 0,
        descuentoActivo,
        porcentajeDescuento: descuentoActivo ? porcentajeDescuento : 0,
        nombreDescuento: descuentoActivo ? nombreDescuento : undefined,
        nombreProyecto,
        notas,
        precioM2: firstPrecioM2,
        controladorId: selectedControlador?.id,
        precioControlador: selectedControlador ? precioControlador : undefined,
        adicionales: adicionales.filter((a) => a.descripcion.trim()),
        detalles: validItems.map((li) => ({
          productoId: li.tipo === 'GABINETE' ? li.producto!.id : undefined,
          pantallaModularId: li.tipo === 'MODULAR' ? li.pantallaModular!.id : undefined,
          columnas: li.columnas,
          filas: li.filas,
          rotado: li.rotado,
          precioM2: li.precioM2,
          cantidad: Math.max(1, li.cantidad || 1),
        })),
      };

      const saved = await cotizacionesApi.create(request);
      toast.success(`Cotización #${saved.id} guardada. Generando PDF...`);

      setGeneratingPdf(true);
      try {
        await generateCotizacionPdf(saved);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error desconocido';
        toast.error(`Cotización guardada pero hubo un error al generar el PDF: ${msg}`);
        console.error('PDF generation error:', err);
      } finally {
        setGeneratingPdf(false);
      }

      // Reset form
      setDni('');
      setClienteNombre('');
      setClienteRubro('');
      setClienteCorreo('');
      setClienteAtencion('');
      setClienteTelefono('');
      setClienteCiudad('');
      setClienteRepresentante('');
      setClienteCargoRepresentante('');
      setTipoOperacion('VENTA');
      setUbicacion('LIMA');
      setCostoEnvio(0);
      setDescuentoActivo(false);
      setPorcentajeDescuento(0);
      setNombreDescuento('');
      setNombreProyecto('');
      setNotas('');
      setLineItems([newPantalla()]);
      setSelectedControlador(null);
      setPrecioControlador(0);
      setTipoControladorFiltro('SINCRONO');
      setAdicionales([]);
    } catch {
      toast.error('Error al guardar la cotización');
    } finally {
      setSaving(false);
    }
  };

  const anyProductSelected = lineItems.some((li) => li.producto || li.pantallaModular);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Nueva Cotización</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Client + Config */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Datos del Cliente</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DNI / RUC</label>
                <div className="relative">
                  <input
                    type="text"
                    value={dni}
                    onChange={(e) => handleDniChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Ingrese DNI o RUC"
                  />
                  {dniLoading && (
                    <span className="absolute right-3 top-2.5 text-xs text-gray-400">Buscando...</span>
                  )}
                </div>
                {cliente && (
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {cliente.esClienteRecurrente && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        Cliente recurrente
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                      {cliente.totalCotizaciones}{' '}
                      {cliente.totalCotizaciones === 1 ? 'cotización' : 'cotizaciones'} previas
                    </span>
                    {cliente.ultimoCotizador && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                        Atendido por: {cliente.ultimoCotizador}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={clienteNombre}
                  onChange={(e) => setClienteNombre(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rubro</label>
                <input
                  type="text"
                  value={clienteRubro}
                  onChange={(e) => setClienteRubro(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo</label>
                <input
                  type="email"
                  value={clienteCorreo}
                  onChange={(e) => setClienteCorreo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Atención</label>
                <input
                  type="text"
                  value={clienteAtencion}
                  onChange={(e) => setClienteAtencion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={clienteTelefono}
                  onChange={(e) => setClienteTelefono(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                <input
                  type="text"
                  value={clienteCiudad}
                  onChange={(e) => setClienteCiudad(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              {dni.length === 11 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Representante</label>
                    <input
                      type="text"
                      placeholder="Persona de contacto"
                      value={clienteRepresentante}
                      onChange={(e) => setClienteRepresentante(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
                    <input
                      type="text"
                      placeholder="Cargo en la empresa"
                      value={clienteCargoRepresentante}
                      onChange={(e) => setClienteCargoRepresentante(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Pantallas (multi) */}
          {lineItems.map((pantalla, idx) => {
            const calc = calculations.itemCalculations[idx] ?? {
              cantidadGabinetes: 0, anchoTotalM: 0, altoTotalM: 0, areaM2: 0,
              precioUnitario: 0, subtotalProducto: 0, subtotalPantalla: 0,
            };
            const hasProduct = pantalla.producto || pantalla.pantallaModular;
            return (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                  <h3 className="text-lg font-semibold text-gray-900">Pantalla #{idx + 1}</h3>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => movePantalla(idx, -1)}
                      disabled={idx === 0}
                      title="Mover arriba"
                      className="px-2 py-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => movePantalla(idx, 1)}
                      disabled={idx === lineItems.length - 1}
                      title="Mover abajo"
                      className="px-2 py-1 text-gray-500 hover:text-gray-800 disabled:opacity-30"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      onClick={() => removePantalla(idx)}
                      disabled={lineItems.length <= 1}
                      title="Eliminar pantalla"
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                <div className="flex gap-4 mb-4 flex-wrap">
                  <button
                    onClick={() => { setActiveLineIndex(idx); setShowProductModal(true); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    {hasProduct ? 'Cambiar Selección' : 'Seleccionar Gabinete / Módulo'}
                  </button>
                  {pantalla.tipo === 'GABINETE' && pantalla.producto && (
                    <span className="flex items-center gap-2 text-sm text-gray-600">
                      {pantalla.producto.nombre} ({pantalla.producto.tipo})
                      <StockAvailabilityBadge
                        disponibilidad={dispProductosMap.get(pantalla.producto.id)}
                        demanded={demandaPorProducto.get(pantalla.producto.id)}
                      />
                    </span>
                  )}
                  {pantalla.tipo === 'MODULAR' && pantalla.pantallaModular && (
                    <span className="flex items-center gap-2 text-sm text-gray-600">
                      {pantalla.pantallaModular.nombre} — Modular ({pantalla.pantallaModular.tipo})
                      <StockAvailabilityBadge
                        disponibilidad={dispModularesMap.get(pantalla.pantallaModular.id)}
                        demanded={demandaPorModular.get(pantalla.pantallaModular.id)}
                      />
                    </span>
                  )}
                </div>

                {hasProduct && (
                  <>
                    {/* Desired dimensions in meters */}
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ancho deseado (m)</label>
                        <input
                          type="number"
                          step={0.01}
                          value={pantalla.anchoDeseadoM || ''}
                          onChange={(e) => handleAnchoDeseado(idx, parseFloat(e.target.value))}
                          onBlur={() => updateLineItem(idx, { anchoDeseadoM: isNaN(pantalla.anchoDeseadoM) || pantalla.anchoDeseadoM < 0 ? 0 : pantalla.anchoDeseadoM })}
                          placeholder="Ej: 5.4"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Alto deseado (m)</label>
                        <input
                          type="number"
                          step={0.01}
                          value={pantalla.altoDeseadoM || ''}
                          onChange={(e) => handleAltoDeseado(idx, parseFloat(e.target.value))}
                          onBlur={() => updateLineItem(idx, { altoDeseadoM: isNaN(pantalla.altoDeseadoM) || pantalla.altoDeseadoM < 0 ? 0 : pantalla.altoDeseadoM })}
                          placeholder="Ej: 3.0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>
                    </div>

                    {/* Columns & Rows stepper */}
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Columnas</label>
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => stepLineItem(idx, 'columnas', -1)}
                            disabled={pantalla.columnas <= 1}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-gray-600 font-bold"
                          >
                            −
                          </button>
                          <span className="flex-1 text-center py-2 font-medium text-gray-900">{isNaN(pantalla.columnas) ? 1 : pantalla.columnas}</span>
                          <button
                            type="button"
                            onClick={() => stepLineItem(idx, 'columnas', 1)}
                            disabled={pantalla.columnas >= 100}
                            className="px-3 py-2 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 text-blue-600 font-bold"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filas</label>
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => stepLineItem(idx, 'filas', -1)}
                            disabled={pantalla.filas <= 1}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-gray-600 font-bold"
                          >
                            −
                          </button>
                          <span className="flex-1 text-center py-2 font-medium text-gray-900">{isNaN(pantalla.filas) ? 1 : pantalla.filas}</span>
                          <button
                            type="button"
                            onClick={() => stepLineItem(idx, 'filas', 1)}
                            disabled={pantalla.filas >= 100}
                            className="px-3 py-2 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 text-blue-600 font-bold"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    {pantalla.tipo === 'MODULAR' && pantalla.pantallaModular &&
                      pantalla.pantallaModular.anchoModuloMm !== pantalla.pantallaModular.altoModuloMm && (
                      <div className="mb-3">
                        <button
                          type="button"
                          onClick={() => updateLineItem(idx, { rotado: !pantalla.rotado })}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            pantalla.rotado
                              ? 'bg-blue-100 border-blue-400 text-blue-700'
                              : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {pantalla.rotado ? 'Módulo rotado 90° (vertical)' : 'Rotar módulo 90°'}
                        </button>
                      </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-gray-500">{pantalla.tipo === 'MODULAR' ? 'Módulos' : 'Gabinetes'}</p>
                        <p className="font-bold text-lg">{calc.cantidadGabinetes}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-gray-500">Ancho</p>
                        <p className="font-bold text-lg">{formatDimension(calc.anchoTotalM)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-gray-500">Alto</p>
                        <p className="font-bold text-lg">{formatDimension(calc.altoTotalM)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-gray-500">Área</p>
                        <p className="font-bold text-lg">{formatArea(calc.areaM2)}</p>
                      </div>
                    </div>

                    <CanvasBoard
                      columnas={pantalla.columnas}
                      filas={pantalla.filas}
                      anchoCm={
                        pantalla.tipo === 'MODULAR'
                          ? (pantalla.rotado ? pantalla.pantallaModular!.altoModuloMm : pantalla.pantallaModular!.anchoModuloMm) / 10
                          : pantalla.producto!.anchoCm
                      }
                      altoCm={
                        pantalla.tipo === 'MODULAR'
                          ? (pantalla.rotado ? pantalla.pantallaModular!.anchoModuloMm : pantalla.pantallaModular!.altoModuloMm) / 10
                          : pantalla.producto!.altoCm
                      }
                      tipo={
                        pantalla.tipo === 'MODULAR'
                          ? pantalla.pantallaModular!.tipo
                          : pantalla.producto!.tipo
                      }
                      unidad={pantalla.tipo === 'MODULAR' ? 'módulos' : 'gabinetes'}
                    />

                    {/* Per-pantalla pricing */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Precio por m² (USD)</label>
                        <input
                          type="number"
                          step={0.01}
                          value={pantalla.precioM2 || ''}
                          onChange={(e) => updateLineItem(idx, { precioM2: parseFloat(e.target.value) })}
                          onBlur={() => updateLineItem(idx, { precioM2: isNaN(pantalla.precioM2) || pantalla.precioM2 < 0 ? 0 : pantalla.precioM2 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad de pantallas iguales</label>
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => stepLineItem(idx, 'cantidad', -1)}
                            disabled={pantalla.cantidad <= 1}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-gray-600 font-bold"
                          >
                            −
                          </button>
                          <span className="flex-1 text-center py-2 font-medium text-gray-900">{isNaN(pantalla.cantidad) ? 1 : pantalla.cantidad}</span>
                          <button
                            type="button"
                            onClick={() => stepLineItem(idx, 'cantidad', 1)}
                            disabled={pantalla.cantidad >= 99}
                            className="px-3 py-2 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 text-blue-600 font-bold"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-between items-center text-sm bg-blue-50 rounded-lg px-3 py-2">
                      <span className="text-gray-600">
                        Subtotal pantalla
                        {pantalla.cantidad > 1 && <span className="text-gray-400"> ({pantalla.cantidad} unidades)</span>}
                      </span>
                      <span className="font-bold text-blue-700">{formatCurrency(calc.subtotalPantalla)}</span>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* Add pantalla button */}
          <button
            type="button"
            onClick={addPantalla}
            className="w-full py-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-colors text-sm font-medium"
          >
            + Agregar pantalla
          </button>

          {/* Controlador / Procesador (global) */}
          {anyProductSelected && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Controlador / Procesador de Video</h3>

              {/* Toggle Síncrono / Asíncrono */}
              <div className="flex gap-2 mb-3">
                {(['SINCRONO', 'ASINCRONO'] as const).map((tipo) => (
                  <button
                    key={tipo}
                    type="button"
                    onClick={() => {
                      setTipoControladorFiltro(tipo);
                      setSelectedControlador(null);
                      setPrecioControlador(0);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      tipoControladorFiltro === tipo
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tipo === 'SINCRONO' ? 'Síncrono' : 'Asíncrono'}
                  </button>
                ))}
              </div>

              {totalPixels > 0 && (
                <p className="text-xs text-gray-500 mb-3">
                  Píxeles totales: <span className="font-bold text-gray-700">{totalPixels.toLocaleString()}</span>
                  {tipoControladorFiltro === 'SINCRONO' && requiredPorts > 0 && (
                    <> | <span className="font-bold text-gray-700">{requiredPorts}</span> {requiredPorts === 1 ? 'puerto' : 'puertos'} ethernet requeridos</>
                  )}
                  {recommendedControlador && (
                    <> — Recomendado: <span className="font-bold text-blue-600">{recommendedControlador.nombre}</span></>
                  )}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-600">Controlador</label>
                    {selectedControlador && (
                      <StockAvailabilityBadge
                        disponibilidad={dispControladoresMap.get(selectedControlador.id)}
                        demanded={1}
                      />
                    )}
                  </div>
                  <select
                    value={selectedControlador?.id ?? ''}
                    onChange={(e) => {
                      const id = parseInt(e.target.value);
                      setSelectedControlador(filteredControladores.find((c) => c.id === id) ?? null);
                      setPrecioControlador(0);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">Sin controlador</option>
                    {filteredControladores.map((c) => {
                      const insuficiente = tipoControladorFiltro === 'ASINCRONO'
                        ? (c.capacidadPixel != null && totalPixels > 0 && c.capacidadPixel * 1_000_000 < totalPixels)
                        : (requiredPorts > 0 && c.puertos < requiredPorts);
                      const info = tipoControladorFiltro === 'ASINCRONO'
                        ? (c.capacidadPixel ? `${c.capacidadPixel}M px` : '')
                        : `${c.puertos} puertos`;
                      const disp = dispControladoresMap.get(c.id);
                      const stockTag = disp
                        ? (disp.estado === 'SIN_STOCK'
                            ? ' · sin stock'
                            : disp.estado === 'LIMITADO'
                            ? ' · stock limitado'
                            : '')
                        : '';
                      return (
                        <option key={c.id} value={c.id}>
                          {c.nombre} ({info}){insuficiente ? ' — insuficiente' : ''}{stockTag}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Precio controlador (USD)</label>
                  <input
                    type="number"
                    step={0.01}
                    value={precioControlador || ''}
                    onChange={(e) => setPrecioControlador(parseFloat(e.target.value))}
                    onBlur={() => setPrecioControlador((v) => isNaN(v) || v < 0 ? 0 : v)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
              {selectedControlador?.imagen && (
                <img
                  src={resolveImageUrl(selectedControlador.imagen)!}
                  alt={selectedControlador.nombre}
                  className="h-20 object-contain rounded bg-white border border-gray-200 p-1 mt-3"
                />
              )}
              {selectedControlador && tipoControladorFiltro === 'ASINCRONO' && selectedControlador.capacidadPixel != null && totalPixels > 0 && selectedControlador.capacidadPixel * 1_000_000 < totalPixels && (
                <p className="text-xs text-red-600 font-medium mt-2">
                  Este controlador no tiene suficiente capacidad de píxeles ({selectedControlador.capacidadPixel}M) para la configuración actual ({(totalPixels / 1_000_000).toFixed(2)}M)
                </p>
              )}
              {selectedControlador && tipoControladorFiltro === 'SINCRONO' && requiredPorts > 0 && selectedControlador.puertos < requiredPorts && (
                <p className="text-xs text-red-600 font-medium mt-2">Este controlador no tiene suficientes puertos para la configuración actual</p>
              )}
            </div>
          )}

          {/* Adicionales (global) */}
          {anyProductSelected && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Adicionales</h3>
              <div className="space-y-2">
                {adicionales.map((adic, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Descripción (ej. Estructura)"
                      value={adic.descripcion}
                      onChange={(e) => setAdicionales((prev) => prev.map((a, idxA) => idxA === i ? { ...a, descripcion: e.target.value } : a))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <input
                      type="number"
                      step={0.01}
                      placeholder="Precio"
                      value={adic.precio || ''}
                      onChange={(e) => setAdicionales((prev) => prev.map((a, idxA) => idxA === i ? { ...a, precio: parseFloat(e.target.value) || 0 } : a))}
                      onBlur={() => setAdicionales((prev) => prev.map((a, idxA) => idxA === i ? { ...a, precio: isNaN(a.precio) || a.precio < 0 ? 0 : a.precio } : a))}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <input
                      type="number"
                      placeholder="Cant."
                      value={adic.cantidad || ''}
                      onChange={(e) => setAdicionales((prev) => prev.map((a, idxA) => idxA === i ? { ...a, cantidad: parseInt(e.target.value) || 1 } : a))}
                      onBlur={() => setAdicionales((prev) => prev.map((a, idxA) => idxA === i ? { ...a, cantidad: isNaN(a.cantidad) || a.cantidad < 1 ? 1 : a.cantidad } : a))}
                      className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <button
                      onClick={() => setAdicionales((prev) => prev.filter((_, idxA) => idxA !== i))}
                      className="text-gray-400 hover:text-red-500 text-lg leading-none px-1"
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 items-center flex-wrap">
                  <select
                    value=""
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      setAdicionales((prev) => [...prev, { descripcion: val, precio: 0, cantidad: 1 }]);
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none text-gray-600"
                  >
                    <option value="">+ Agregar predefinido...</option>
                    <option value="Estructura tipo Rack">Estructura tipo Rack</option>
                    <option value="Estructura tipo Canastilla">Estructura tipo Canastilla</option>
                    <option value="Estructura Torre Unipolar">Estructura Torre Unipolar</option>
                    <option value="Kit de repuestos (10% de cantidad de módulos-tarjetas-fuentes)">Kit de repuestos (10% módulos-tarjetas-fuentes)</option>
                    <option value="Tablero">Tablero</option>
                    <option value="Mini PC NUC">Mini PC NUC</option>
                    <option value="Tablero de distribución">Tablero de distribución</option>
                    <option value="Tablero de mando para transmisor y router">Tablero de mando para transmisor y router</option>
                  </select>
                  <button
                    onClick={() => setAdicionales((prev) => [...prev, { descripcion: '', precio: 0, cantidad: 1 }])}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    + Adicional manual
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Project info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Información del Proyecto</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Proyecto</label>
                <input
                  type="text"
                  value={nombreProyecto}
                  onChange={(e) => setNombreProyecto(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas Adicionales</label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Pricing */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 lg:sticky lg:top-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cálculo de Precios</h3>

            <div className="space-y-4">
              {/* Operation type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Operación</label>
                <div className="flex gap-2">
                  {(['VENTA', 'ALQUILER'] as const).map((tipo) => (
                    <button
                      key={tipo}
                      onClick={() => setTipoOperacion(tipo)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                        tipoOperacion === tipo
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {tipo === 'VENTA' ? 'Compra' : 'Alquiler'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ubicación</label>
                <div className="flex gap-2">
                  {(['LIMA', 'PROVINCIA'] as const).map((ub) => (
                    <button
                      key={ub}
                      onClick={() => setUbicacion(ub)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                        ubicacion === ub
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {ub === 'LIMA' ? 'Lima' : 'Provincia'}
                    </button>
                  ))}
                </div>
              </div>

              {ubicacion === 'PROVINCIA' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Costo de Envío (USD)</label>
                  <input
                    type="number"
                    step={0.01}
                    value={costoEnvio || ''}
                    onChange={(e) => setCostoEnvio(parseFloat(e.target.value))}
                    onBlur={() => setCostoEnvio((v) => isNaN(v) || v < 0 ? 0 : v)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              )}

              {/* Discount */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={descuentoActivo}
                    onChange={(e) => setDescuentoActivo(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Aplicar Descuento
                </label>
              </div>

              {descuentoActivo && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Porcentaje (%)</label>
                    <input
                      type="number"
                      max={100}
                      value={porcentajeDescuento || ''}
                      onChange={(e) => setPorcentajeDescuento(parseFloat(e.target.value))}
                      onBlur={() => setPorcentajeDescuento((v) => isNaN(v) || v < 0 ? 0 : Math.min(100, v))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Descuento</label>
                    <input
                      type="text"
                      value={nombreDescuento}
                      onChange={(e) => setNombreDescuento(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                {calculations.itemCalculations.map((c, i) => {
                  const li = lineItems[i];
                  if (!li || (!li.producto && !li.pantallaModular)) return null;
                  const label = li.cantidad > 1
                    ? `Pantalla #${i + 1} × ${li.cantidad}`
                    : `Pantalla #${i + 1}`;
                  return (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-medium">{formatCurrency(c.subtotalPantalla)}</span>
                    </div>
                  );
                })}
                <div className="flex justify-between text-sm border-t border-gray-100 pt-2">
                  <span className="text-gray-500">Subtotal pantallas</span>
                  <span className="font-medium">{formatCurrency(calculations.subtotal)}</span>
                </div>
                {calculations.precioControlador > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Controlador/procesador</span>
                    <span className="font-medium">{formatCurrency(calculations.precioControlador)}</span>
                  </div>
                )}
                {calculations.sumaAdicionales > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Adicionales</span>
                    <span className="font-medium">{formatCurrency(calculations.sumaAdicionales)}</span>
                  </div>
                )}
                {descuentoActivo && calculations.descuento > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Descuento ({porcentajeDescuento}%)</span>
                    <span>-{formatCurrency(calculations.descuento)}</span>
                  </div>
                )}
                {ubicacion === 'PROVINCIA' && costoEnvio > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Envío</span>
                    <span>{formatCurrency(costoEnvio)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                  <span>Total</span>
                  <span className="text-blue-600">{formatCurrency(calculations.totalFinal)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-2">
                {stockErrors.length > 0 && (
                  <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-700">
                    <p className="font-semibold mb-1">Stock insuficiente</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {stockErrors.map((e, i) => (
                        <li key={i}>{e.nombre}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving || generatingPdf || stockErrors.length > 0}
                  className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving
                    ? 'Guardando...'
                    : generatingPdf
                    ? 'Generando PDF...'
                    : stockErrors.length > 0
                    ? 'Stock insuficiente'
                    : 'Guardar y Descargar PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Selection Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-3xl max-h-[90vh] sm:max-h-[80vh] overflow-y-auto p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold">Seleccionar Gabinete / Módulo</h3>
                <select
                  value={tipoSeleccion}
                  onChange={(e) => setTipoSeleccion(e.target.value as 'GABINETE' | 'MODULAR')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="GABINETE">Gabinetes</option>
                  <option value="MODULAR">Modulares</option>
                </select>
              </div>
              <button
                onClick={() => setShowProductModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            {tipoSeleccion === 'GABINETE' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {productos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => selectProduct(p)}
                    className="text-left border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    {p.imagenCabinet && (
                      <img
                        src={resolveImageUrl(p.imagenCabinet)!}
                        alt={p.nombre}
                        className="w-full h-32 object-contain rounded mb-2 bg-gray-50"
                      />
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-gray-900">{p.nombre}</p>
                      <StockAvailabilityBadge disponibilidad={dispProductosMap.get(p.id)} className="shrink-0" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{p.codigo} - {p.tipo}</p>
                    <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                      <p>Pixel Pitch: {p.pixelPitch}mm</p>
                      <p>Dimensiones: {p.anchoCm}x{p.altoCm} cm</p>
                      {p.brilloNits && <p>Brillo: {p.brilloNits} nits</p>}
                    </div>
                  </button>
                ))}
                {productos.length === 0 && (
                  <p className="col-span-2 text-center text-gray-500 py-8">No hay gabinetes disponibles</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {pantallasModulares.map((pm) => (
                  <button
                    key={pm.id}
                    onClick={() => selectPantallaModular(pm)}
                    className="text-left border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    {pm.imagen && (
                      <img
                        src={resolveImageUrl(pm.imagen)!}
                        alt={pm.nombre}
                        className="w-full h-32 object-contain rounded mb-2 bg-gray-50"
                      />
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-gray-900">{pm.nombre}</p>
                      <StockAvailabilityBadge disponibilidad={dispModularesMap.get(pm.id)} className="shrink-0" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{pm.codigo} - {pm.tipo}</p>
                    <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                      <p>Pixel Pitch: {pm.pixelPitch}mm</p>
                      <p>Módulo: {pm.anchoModuloMm}×{pm.altoModuloMm} mm</p>
                      {pm.brilloNits && <p>Brillo: {pm.brilloNits} nits</p>}
                    </div>
                  </button>
                ))}
                {pantallasModulares.length === 0 && (
                  <p className="col-span-2 text-center text-gray-500 py-8">No hay pantallas modulares disponibles</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
